import {
  AlertCircle,
  GripVertical,
  ImageIcon,
  MoreVertical,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import {
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import invariant from 'tiny-invariant'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { useDeleteItem, useUpdateItem } from '../hooks/use-item-mutations'
import { isDerivedRow, isUntracked, rowChip } from '../lib/row-stock'
import { EditItemDialog } from './edit-item-dialog'
import { ItemImagePreviewDialog } from './item-image-preview-dialog'
import { TemperatureToggle } from './temperature-toggle'
import { BufferField } from './buffer-field'
import { DeliveryChip } from './delivery-chip'
import {
  HIDE_NARROW,
  HIDE_TIGHT,
  INK,
  LEDGER_GRID,
  LedgerRule,
  Num,
} from './stock-ledger'
import type { MenuItem } from '@/api/types'
import type { StockActionKind } from '@/features/inventory/components/stock-action-dialog'
import { StockActionDialog } from '@/features/inventory/components/stock-action-dialog'
import { SellableHint } from '@/features/inventory/components/sellable-hint'
import { onHandQty } from '@/features/inventory/lib/stock'
import { pricesApi } from '@/api/endpoints/prices'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'

interface MenuItemRowProps {
  item: MenuItem
  menuId: number
  sectionId: number
  restaurantId: number
  /** The menu's own day — what the ledger's incoming column and the row's chip are about. */
  menuDate?: string
  canReceiveStock: boolean
  canAdjustStock: boolean
  canEditProduct: boolean
  /**
   * Whether the LOCATION tracks inventory at all (`restaurants.inventory_tracking_enabled`).
   *
   * Needed because paydesk has no per-PRODUCT tracking flag yet: without one, "no inventory row"
   * means "sell freely" at a location that tracks nothing and "the shop shows 0" at one that
   * does. Same data, opposite meaning. Drop this prop the day products carry their own flag.
   */
  tracksInventory: boolean
}

/** The editable value only — the symbol sits beside the field, as it did before. */
const PRICE_FORMAT = new Intl.NumberFormat('de-AT', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: false,
})

function eurosToCents(value: string): number | null {
  const cleaned = value.trim().replace(',', '.')
  if (cleaned === '') return null
  const n = Number(cleaned)
  if (!Number.isFinite(n) || n < 0) return null
  return Math.round(n * 100)
}

export function MenuItemRow({
  item,
  menuId,
  sectionId,
  restaurantId,
  menuDate,
  canReceiveStock,
  canAdjustStock,
  canEditProduct,
  tracksInventory,
}: MenuItemRowProps) {
  const { t: tMenus } = useTranslation('menus')
  const { t: tInventory } = useTranslation('inventory')
  const [editOpen, setEditOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [stockAction, setStockAction] = useState<StockActionKind | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [closestEdge, setClosestEdge] = useState<string | null>(null)

  const itemRef = useRef<HTMLDivElement>(null)
  const dragHandleRef = useRef<HTMLButtonElement>(null)

  const deleteItem = useDeleteItem()
  const updateItem = useUpdateItem()
  const queryClient = useQueryClient()

  // Current per-restaurant price for inline editing.
  const { data: price } = useQuery({
    queryKey: ['product-price', item.sku, restaurantId],
    queryFn: () => pricesApi.get(item.sku, restaurantId),
    enabled: !!item.sku && !!restaurantId,
    retry: false,
  })

  const [nameInput, setNameInput] = useState(item.overrideName ?? '')
  const [priceInput, setPriceInput] = useState('')

  useEffect(() => {
    setNameInput(item.overrideName ?? '')
  }, [item.overrideName])

  useEffect(() => {
    if (price?.amountCents != null) {
      // de-AT decimals (10,90), not toFixed's 10.90. Not formatCurrency(): that appends the €
      // symbol, which would then have to be parsed back out on every keystroke.
      setPriceInput(PRICE_FORMAT.format(price.amountCents / 100))
    }
  }, [price?.amountCents])

  const setPrice = useMutation({
    mutationFn: (cents: number) => pricesApi.set(item.sku, restaurantId, cents),
    onSuccess: () => {
      toast.success(tMenus('itemRow.priceSaved'))
      queryClient.invalidateQueries({
        queryKey: ['product-price', item.sku, restaurantId],
      })
      queryClient.invalidateQueries({ queryKey: ['menus', menuId, 'contents'] })
    },
    onError: () => toast.error(tMenus('itemRow.priceSaveFailed')),
  })

  useEffect(() => {
    const itemEl = itemRef.current
    const handleEl = dragHandleRef.current
    invariant(itemEl)
    invariant(handleEl)

    return combine(
      draggable({
        element: itemEl,
        dragHandle: handleEl,
        getInitialData: () => ({
          type: 'item',
          sectionId,
          productId: item.productId,
          item,
        }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element: itemEl,
        canDrop: ({ source }) =>
          source.data.type === 'item' || source.data.type === 'product',
        getData: ({ input, element }) =>
          attachClosestEdge(
            { type: 'item', sectionId, productId: item.productId },
            { input, element, allowedEdges: ['top', 'bottom'] },
          ),
        onDragEnter: ({ self }) =>
          setClosestEdge(extractClosestEdge(self.data)),
        onDrag: ({ self }) => setClosestEdge(extractClosestEdge(self.data)),
        onDragLeave: () => setClosestEdge(null),
        onDrop: () => setClosestEdge(null),
      }),
    )
  }, [item.productId, sectionId])

  const style = {
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
  }

  const displayName = item.overrideName || item.baseName

  const handleDelete = () => {
    if (confirm(tMenus('itemRow.confirmRemove', { name: displayName }))) {
      deleteItem.mutate({ menuId, sectionId, productId: item.productId })
    }
  }

  const commitName = () => {
    const trimmed = nameInput.trim()
    if (trimmed === (item.overrideName ?? '')) return
    if (trimmed === '') return // clearing the override is done via the edit dialog
    updateItem.mutate({
      menuId,
      sectionId,
      productId: item.productId,
      data: { overrideName: trimmed },
    })
  }

  const commitPrice = () => {
    const cents = eurosToCents(priceInput)
    if (cents == null) return
    if (price?.amountCents === cents) return
    setPrice.mutate(cents)
  }

  // A dish, variant or not-tracked article (CHG-2026-09-stock-usage-and-price-twins, task 11.6):
  // no stock of its own, so no terms, no buffer and no stock actions — only the online number of
  // its tracked parts and the part that limits it.
  const derived = isDerivedRow(item)
  const untracked = !derived && isUntracked(item)
  const offersStockActions = (canAdjustStock || canReceiveStock) && !derived

  return (
    <>
      <div
        ref={itemRef}
        style={style}
        className={cn(
          LEDGER_GRID,
          'rounded-lg border bg-card p-3 transition-colors hover:bg-muted/50',
        )}
      >
        {closestEdge === 'top' && (
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary z-10" />
        )}
        {closestEdge === 'bottom' && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary z-10" />
        )}
        <div className="flex min-w-0 items-center gap-3">
          <button
            ref={dragHandleRef}
            className="cursor-grab touch-none active:cursor-grabbing"
          >
            <GripVertical className="w-4 h-4 text-muted-foreground hover:text-foreground" />
          </button>

          {item.thumbUrl ? (
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="h-10 w-10 shrink-0 overflow-hidden rounded focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={tMenus('itemRow.showImage')}
            >
              <img
                src={item.thumbUrl}
                alt=""
                loading={'lazy' as const}
                className="h-full w-full object-cover"
              />
            </button>
          ) : (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-muted">
              <ImageIcon className="w-4 h-4 text-muted-foreground" />
            </div>
          )}

          <div className="min-w-0 flex-1 space-y-1">
            <Input
              value={nameInput}
              placeholder={item.baseName}
              onChange={(e) => setNameInput(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur()
              }}
              className="h-8 font-medium"
              aria-label={tMenus('itemRow.displayName')}
            />
            <div className="flex min-w-0 items-center gap-2">
              <p className="shrink-0 text-xs text-muted-foreground">
                {tMenus('itemRow.sku')} {item.sku}
              </p>
              {item.internalName && item.internalName !== item.baseName && (
                <p
                  className="min-w-0 truncate text-xs text-muted-foreground/60"
                  title={`${tMenus('itemRow.internalName')} ${item.internalName}`}
                >
                  · {item.internalName}
                </p>
              )}
              <DeliveryChip
                chip={rowChip(item, tracksInventory)}
                menuDate={menuDate}
                restaurantId={restaurantId}
              />
              {derived && item.stockKind && item.stockKind !== 'article' && (
                <Badge variant="outline" className="text-xs">
                  {tInventory(`kind.${item.stockKind}`)}
                </Badge>
              )}
              {!item.active && (
                <Badge variant="secondary" className="text-xs">
                  {tMenus('itemRow.inactive')}
                </Badge>
              )}
              {item.priceMissing && (
                <Badge variant="destructive" className="text-xs">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  {tMenus('itemRow.noPrice')}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* self-start puts the toggle on the input's line. Centred, it floated between the name
            and the SKU line below it and read as belonging to neither. */}
        <TemperatureToggle
          className={cn(HIDE_TIGHT, 'self-start')}
          sku={item.sku}
          value={item.temperatureChoice !== false}
          menuId={menuId}
          canEdit={canEditProduct}
        />

        {/* Absorbs the surplus width so the name stays readable and the ledger stays legible. */}
        <div className={HIDE_NARROW} />

        {derived ? (
          <>
            {/* The four terms have no meaning here: the product holds no stock of its own. */}
            <Num className={cn(HIDE_NARROW, 'text-muted-foreground/50')}>—</Num>
            <Num className={cn(HIDE_NARROW, 'text-muted-foreground/50')}>—</Num>
            <Num className={cn(HIDE_NARROW, 'text-muted-foreground/50')}>—</Num>
            <Num className={cn(HIDE_NARROW, 'text-muted-foreground/50')}>—</Num>
            <LedgerRule />
            <div
              className="text-right leading-tight"
              title={tInventory('derived.onlineHint')}
            >
              {item.unlimited || item.sellableQty == null ? (
                <p className="text-xs text-muted-foreground">
                  {item.stockKind === 'not_tracked'
                    ? tInventory('derived.notTracked')
                    : tInventory('derived.unlimited')}
                </p>
              ) : (
                <>
                  <p
                    className={cn(
                      'text-[17px] font-bold tabular-nums leading-none',
                      item.sellableQty === 0 && 'text-destructive',
                    )}
                  >
                    {formatStockDisplay(item.sellableQty)}
                  </p>
                  {(item.limitingName || item.limitingSku) && (
                    <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                      {tInventory('derived.limitedBy', {
                        name: item.limitingName ?? item.limitingSku,
                      })}
                    </p>
                  )}
                </>
              )}
            </div>
          </>
        ) : untracked ? (
          // Not "everything is zero" — there is no inventory row at all. Four dashes would claim
          // the opposite of what a missing row means.
          <div
            className={cn(
              'col-span-6 hidden pr-1 text-right text-xs @[800px]:block',
              tracksInventory ? 'text-destructive' : 'text-muted-foreground/70',
            )}
          >
            {tMenus(
              tracksInventory ? 'itemRow.neverBooked' : 'itemRow.untracked',
            )}
          </div>
        ) : (
          <>
            {/* Bestand is what is physically on the shelf: available PLUS what open orders
                reserve. Rendering availableQty here would make the visible arithmetic wrong —
                18 − 6 − 4 reads as 8 while the row's answer is 14. */}
            <Num className={HIDE_NARROW}>
              {formatStockDisplay(
                onHandQty(item.availableQty, item.reservedQty),
              )}
            </Num>
            {/* The sign carries the operation — the "−" and "+" columns cost 82px the product
                name needs more (CHG-2026-09-menu-editor-redesign, measured on a 1600px window). */}
            <Num className={cn(HIDE_NARROW, INK.reserved)}>
              {item.reservedQty
                ? `−${formatStockDisplay(item.reservedQty)}`
                : '—'}
            </Num>
            <Num
              className={cn(
                HIDE_NARROW,
                item.incomingForDateQty
                  ? INK.incoming
                  : 'text-muted-foreground/50',
              )}
            >
              {item.incomingForDateQty
                ? `+${formatStockDisplay(item.incomingForDateQty)}`
                : '—'}
            </Num>
            {/* text-right is what actually aligns the buffer: its editable state is an inline
                button, not a block, so it would otherwise start at the left of the track. */}
            <div className={cn(HIDE_NARROW, 'text-right')}>
              <BufferField
                sku={item.sku}
                restaurantId={restaurantId}
                menuId={menuId}
                value={item.safetyBufferQty ?? 0}
                canEdit={canAdjustStock}
              />
            </div>
            <LedgerRule />
            <SellableHint
              sku={item.sku}
              restaurantId={restaurantId}
              sellableQty={item.sellableQty ?? 0}
              incomingQty={item.incomingForDateQty ?? 0}
              className={cn(
                'text-right text-[17px] font-bold tabular-nums leading-none',
                item.sellableQty === 0 && 'text-destructive',
              )}
              valueOnly
            />
          </>
        )}

        <div className="flex items-center justify-end gap-1">
          <Input
            value={priceInput}
            onChange={(e) => setPriceInput(e.target.value)}
            onBlur={commitPrice}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
            }}
            inputMode="decimal"
            placeholder="0,00"
            className="h-8 w-[70px] text-right"
            aria-label={tMenus('itemRow.price')}
          />
          <span className="text-sm text-muted-foreground">€</span>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setEditOpen(true)}>
              {tMenus('itemRow.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleDelete}
              className="text-destructive"
            >
              {tMenus('itemRow.remove')}
            </DropdownMenuItem>
            {offersStockActions && (
              <>
                <DropdownMenuSeparator />
                {canReceiveStock && (
                  <DropdownMenuItem onClick={() => setStockAction('receive')}>
                    {tInventory('actions.receive')}
                  </DropdownMenuItem>
                )}
                {canAdjustStock && (
                  <>
                    <DropdownMenuItem
                      onClick={() => setStockAction('setOnHand')}
                    >
                      {tInventory('actions.setOnHand')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setStockAction('setBuffer')}
                    >
                      {tInventory('actions.setBuffer')}
                    </DropdownMenuItem>
                  </>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <EditItemDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        menuId={menuId}
        sectionId={sectionId}
        item={item}
      />

      {item.imageUrl && (
        <ItemImagePreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          imageUrl={item.imageUrl}
          name={displayName}
          sku={item.sku}
        />
      )}

      {stockAction && (
        <StockActionDialog
          open
          onOpenChange={(open) => {
            if (!open) setStockAction(null)
          }}
          action={stockAction}
          sku={item.sku}
          name={displayName}
          restaurantId={restaurantId}
          onSuccess={() =>
            queryClient.invalidateQueries({
              queryKey: ['menus', menuId, 'contents'],
            })
          }
        />
      )}
    </>
  )
}
